// One idempotency key per *logical* mutation attempt. The key is minted when a user
// commits to an action (e.g. when the bulk preview is shown) and reused across manual
// retries, so a partially-applied write is de-duplicated server-side (G-6).
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Composes a multi-field fingerprint. JSON encoding keeps the field boundaries
 * explicit, so no combination of values can shift text from one field into the
 * next and collide with a different submission (`"a", "bc"` vs `"ab", "c"`).
 */
export function fingerprintOf(...parts: Array<string | number>): string {
  return JSON.stringify(parts);
}

export interface KeyRotator {
  /** Same fingerprint → same key (safe retry); changed fingerprint → fresh key. */
  key: (fingerprint: string) => string;
  /** Forget the last fingerprint, e.g. when a dialog reopens for a new operation. */
  reset: () => void;
}

/**
 * Keeps one key per *logical* operation when a form can be retried after a failure.
 * Retrying an unchanged submission replays server-side; changing a material input
 * (a price, a recipient) makes it a different operation and mints a new key.
 */
export function createKeyRotator(): KeyRotator {
  let current = newIdempotencyKey();
  let seen: string | null = null;
  return {
    key(fingerprint) {
      if (seen !== fingerprint) {
        seen = fingerprint;
        current = newIdempotencyKey();
      }
      return current;
    },
    reset() {
      seen = null;
      current = newIdempotencyKey();
    }
  };
}
